/**
 * The world Toukky suggests: twelve cities on every continent, each with the
 * airport code the flight search takes, its country and a photo of that city
 * checked by eye.
 *
 * - `code` is the airport the search opens (`/flights?destination_city=CODE`);
 *   `airports` lists the city's other airports, so a fare to Orly still
 *   belongs to Paris and a visitor leaving from Orly is "already in Paris".
 *   Codes are written out, never derived from a name (deriving "DUB" from
 *   "Dubai" once sent travellers to Dublin).
 * - `city` is the English name, the key for the stays Toukky lists there; the
 *   name shown is in i18n/world.js, the country from Intl.DisplayNames.
 * - `photo` is an Unsplash or Pexels address already cropped to 3:4, which
 *   lib/images.js resizes for each card, or a 600 x 800 file of Toukky's own
 *   in public/destinations, drawn as it is. What each one shows, and whose
 *   photograph it is, is said beside it; the owner chose them (2026-10-04).
 *   Unsplash takes a focal point (fp-x, fp-y, fp-z) where the centre crop
 *   would leave the subject out of the card or under its caption.
 * - `lift` (optional, Pexels has no focal point): the card draws the photo
 *   this many times its height, from its foot, so its top is cut and the
 *   subject rises above the caption at the bottom of the card.
 *
 * Whether Toukky has stays in a city is never written here: it is read at
 * run time from the stays Toukky lists (`stayCities`).
 */
import { worldL } from "@/i18n/world";

const unsplash = (id, extra = "") => `https://images.unsplash.com/${id}?w=600&h=800${extra}`;

export const WORLD_DESTINATIONS = [
  // The Eiffel Tower's silhouette over the Seine at dusk (Vinicius Brasil), the tower in the card.
  { code: "CDG", airports: ["CDG", "ORY", "BVA"], city: "Paris", country: "FR",
    photo: unsplash("photo-1769374343053-9a7a3a6d0c6c", "&fit=crop&crop=focalpoint&fp-x=0.66&fp-y=0.5") },
  // Downtown Dubai and the Burj Khalifa at night, from above (Nejc Soklič).
  { code: "DXB", airports: ["DXB", "DWC"], city: "Dubai", country: "AE", photo: unsplash("photo-1739900292622-a7f860175aad") },
  // The Brooklyn Bridge and Lower Manhattan at night (Yoav Aziz), the bridge's tower in the card.
  { code: "JFK", airports: ["JFK", "EWR", "LGA"], city: "New York", country: "US",
    photo: unsplash("photo-1550909407-4144f2e6b459", "&fit=crop&crop=focalpoint&fp-x=0.36&fp-y=0.5") },
  // Tokyo Tower lit at blue hour among the city's towers (Weichao Deng).
  { code: "NRT", airports: ["NRT", "HND"], city: "Tokyo", country: "JP", photo: unsplash("photo-1698742324562-1c2ed86eb1f6") },
  // The Galata Tower over Beyoğlu at dusk (Ayadi Ghaith).
  { code: "IST", airports: ["IST", "SAW"], city: "Istanbul", country: "TR", photo: unsplash("photo-1637299401875-6b5972fd0275") },
  // Tower Bridge lit at night over the Thames (Flavio Vallone).
  { code: "LHR", airports: ["LHR", "LGW", "STN", "LCY", "LTN"], city: "London", country: "GB", photo: unsplash("photo-1707996215850-03f965acb2ad") },
  // The Koutoubia minaret and its palms in the evening light (Khalid El Hadrami).
  { code: "RAK", airports: ["RAK"], city: "Marrakech", country: "MA", photo: unsplash("photo-1661987284979-23568e5f0023") },
  // Table Mountain and the city from the air (xandro Vandewalle).
  { code: "CPT", airports: ["CPT"], city: "Cape Town", country: "ZA", photo: unsplash("photo-1770988966522-4eea7f83dbe9") },
  // Wat Arun over the river at sunset (SHAN LU).
  { code: "BKK", airports: ["BKK", "DMK"], city: "Bangkok", country: "TH", photo: unsplash("photo-1714672709462-de21a12a1339") },
  // The Colosseum lit at blue hour (Yoav Aziz).
  { code: "FCO", airports: ["FCO", "CIA"], city: "Rome", country: "IT", photo: unsplash("photo-1647954054363-29c5deccc967") },
  // Sugarloaf Mountain and the bay at dusk, from above (Daniel Mok).
  { code: "GIG", airports: ["GIG", "SDU"], city: "Rio de Janeiro", country: "BR", photo: unsplash("photo-1782810510838-f6d1bb8dbf81") },
  // Dakar's corniche at dusk: an image generated for Toukky, since no photograph
  // of the city could be found on Unsplash; the owner chose it (2026-10-04).
  { code: "DSS", airports: ["DSS", "DKR"], city: "Dakar", country: "SN", photo: "/destinations/dakar.webp" },
];

const keyOf = (value) => String(value || "").trim().toLowerCase();

/** Every name a city goes by on the site (its English key and its six translations), lower-cased. */
function namesOf(destination) {
  const names = new Set([keyOf(destination.city)]);
  for (const table of Object.values(worldL)) {
    const name = table.cities?.[destination.code];
    if (name) names.add(keyOf(name));
  }
  return names;
}

/** The world destination an airport code belongs to, or null. */
export function destinationFor(code) {
  const wanted = String(code || "").trim().toUpperCase();
  if (!wanted) return null;
  return WORLD_DESTINATIONS.find((d) => d.airports.includes(wanted)) || null;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const pad = (n) => String(n).padStart(2, "0");
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/**
 * The search form's own default dates (components/SearchWidget): leaving
 * tomorrow, back seven days later, local time. The airlines are only asked
 * for a dated search, so every card and route link carries these.
 */
export function defaultTripDates(now = new Date()) {
  const depart = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const back = new Date(depart.getFullYear(), depart.getMonth(), depart.getDate() + 7);
  return { date: isoDay(depart), return_date: isoDay(back) };
}

/**
 * The flight search a card opens: to the city's airport, from the visitor's
 * airport when one is known and is not in that same city. Without an origin
 * the flights page opens its form on that destination and asks where from
 * (pages/Flights.jsx): the airlines are only asked for a route. `dates`
 * ({ date, return_date }, YYYY-MM-DD, defaultTripDates) dates the search as
 * the search form would; the airlines are only asked for a dated search, so
 * a link without one lists no live flight.
 */
export function flightsLink(destination, origin = "", dates = null) {
  const from = String(origin || "").trim().toUpperCase();
  const q = new URLSearchParams();
  if (/^[A-Z]{3}$/.test(from) && !destination.airports.includes(from)) q.set("origin_city", from);
  q.set("destination_city", destination.code);
  if (dates && ISO_DAY.test(dates.date || "")) {
    q.set("date", dates.date);
    if (ISO_DAY.test(dates.return_date || "") && dates.return_date >= dates.date) {
      q.set("return_date", dates.return_date);
      q.set("trip", "round");
    }
  }
  return `/flights?${q.toString()}`;
}

/** The stays search for a city Toukky lists stays in, by the listings' own spelling of it. */
export const staysLink = (city) => `/hotels?city=${encodeURIComponent(city)}`;

/**
 * The cities Toukky lists stays in, the largest first: { city, region,
 * count, image } where `image` is the first photo one of those stays has.
 * `counted: false` says the list may not hold every stay (a full page of
 * the inventory): the order stays, but `count` is null, since a number
 * read off part of the list would be too low.
 */
export function stayCities(stays = [], { counted = true } = {}) {
  const byCity = new Map();
  for (const stay of stays) {
    const city = String(stay?.city || "").trim();
    if (!city) continue;
    const entry = byCity.get(keyOf(city)) || { city, region: String(stay.region || "").trim(), count: 0, image: null };
    entry.count += 1;
    if (!entry.image) entry.image = (Array.isArray(stay.images) ? stay.images : []).find((u) => typeof u === "string" && u) || null;
    byCity.set(keyOf(city), entry);
  }
  const sorted = [...byCity.values()].sort((a, b) => b.count - a.count || a.city.localeCompare(b.city));
  return counted ? sorted : sorted.map((entry) => ({ ...entry, count: null }));
}

/** The stay city of `cities` (stayCities) that is this destination, under any of its names. */
export function staysFor(destination, cities = []) {
  const names = namesOf(destination);
  return cities.find((entry) => names.has(keyOf(entry.city))) || null;
}

/** The stay cities that are none of the world destinations. */
export function otherStayCities(cities = []) {
  const all = WORLD_DESTINATIONS.map(namesOf);
  return cities.filter((entry) => !all.some((names) => names.has(keyOf(entry.city))));
}

/**
 * The cards, in order: { destination, stays, fare }.
 * - `fares` maps an airport code to a real, dated fare ({ card, section }
 *   from the recommendations); a fare is shown on its city's card, and the
 *   cities with one come first. A fare to a city outside this list is not
 *   turned into a card here.
 * - `cities` are the stay cities (stayCities): a card gets its count, or none.
 */
export function worldCards({ fares, cities = [] } = {}) {
  const fareOf = (destination) => {
    if (!fares) return null;
    for (const code of destination.airports) {
      const fare = fares instanceof Map ? fares.get(code) : fares[code];
      if (fare) return fare;
    }
    return null;
  };
  const cards = WORLD_DESTINATIONS.map((destination) => ({
    destination,
    stays: staysFor(destination, cities),
    fare: fareOf(destination),
  }));
  // Stable: the list's own order within each group.
  return [...cards.filter((c) => c.fare), ...cards.filter((c) => !c.fare)];
}
